(function () {
  const TOOL_SELECTOR = 'fa-galerie-product-tools';
  const PREVIEW_SELECTOR = '[data-fa-galerie-preview]';

  class GalerieProductTools extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = this.readConfig();
      if (!this.config) return;
      this.locale = String(this.config.locale || document.documentElement.lang || 'de-CH');
      this.i18n = { ...(this.config.i18n || {}) };

      this.quantity = 1;
      this.deliveryRequested = false;
      this.deliveryLoading = false;
      this.deliveryError = false;
      this.deliveryText = '';
      this.tierRules = this.normalizeTierRules(this.config.tierRules);
      this.render();
      this.renderLinkedPreviews();
      this.bindStickyBuyBars();
      this.updateStickyBuyBars();
      this.requestDeliveryEstimate();
    }

    readConfig() {
      const script = this.querySelector('[data-fa-galerie-config]');
      if (!script) return null;

      try {
        return JSON.parse(script.textContent || '{}');
      } catch {
        return null;
      }
    }

    normalizeTierRules(rules) {
      if (!Array.isArray(rules)) return [];
      const currency = String(this.config.currency || 'CHF').toUpperCase();

      return rules
        .filter((rule) => rule && rule.active !== false && String(rule.pricingType || 'FIXED_PRICE') === 'FIXED_PRICE')
        .map((rule) => {
          const tiers = Array.isArray(rule.tiers)
            ? rule.tiers
              .map((tier) => {
                const pricingValue = this.readNumber(tier.pricingValue ?? tier.fixedVpePriceCents, 0);
                return {
                  minQuantity: this.readNumber(tier.minQuantity, 0),
                  pricingValue,
                  fixedVpePriceCents: this.readNumber(tier.fixedVpePriceCents ?? pricingValue, pricingValue),
                  currency: String(tier.currency || currency).toUpperCase(),
                  vpeLabel: String(tier.vpeLabel || tier.label || '').trim(),
                };
              })
              .filter((tier) => tier.minQuantity > 0 && tier.pricingValue > 0 && tier.currency === currency)
              .sort((left, right) => left.minQuantity - right.minQuantity)
            : [];

          return {
            priority: this.readNumber(rule.priority, 0),
            tiers,
          };
        })
        .filter((rule) => rule.tiers.length > 0)
        .sort((left, right) => right.priority - left.priority);
    }

    selectedTierRule() {
      return this.tierRules.length ? this.tierRules[0] : null;
    }

    applicableTier(quantity = this.quantity) {
      const rule = this.selectedTierRule();
      if (!rule) return null;

      return rule.tiers.reduce((selected, tier) => (
        quantity >= tier.minQuantity ? tier : selected
      ), null);
    }

    hasTierPrices() {
      return this.selectedTierRule() !== null;
    }

    isInStock() {
      return Boolean(this.config.available) && this.readNumber(this.config.inventoryQuantity, 0) > 0;
    }

    render() {
      const panel = this.querySelector('[data-fa-galerie-panel]');
      if (!panel) return;

      panel.innerHTML = `
        <div class="fa-gp-tools">
          ${this.renderTierSummary()}
          ${this.renderTierControls()}
          ${this.renderProductionStatus()}
          ${this.renderDeliveryStatus()}
        </div>
      `;

      panel.querySelector('[data-fa-gp-quantity]')?.addEventListener('change', (event) => {
        this.setQuantity(event.currentTarget.value, false);
      });

      panel.querySelectorAll('[data-fa-gp-tier]').forEach((button) => {
        button.addEventListener('click', () => this.setQuantity(button.dataset.faGpTier, true));
      });
    }

    renderTierSummary() {
      if (!this.hasTierPrices()) return '';

      const tier = this.applicableTier();
      const selectedUnitPrice = tier?.pricingValue || this.readNumber(this.config.price, 0);
      const totalPrice = selectedUnitPrice * this.quantity;
      const tierLabel = tier ? this.tierLabel(tier) : this.t('normalPrice');

      return `
        <section class="fa-gp-price-box" aria-label="${this.escapeHtml(this.t('selectedTierPrice'))}">
          <ul class="fa-gp-benefits" aria-label="${this.escapeHtml(this.t('benefits'))}">
            <li class="fa-gp-benefit--profile">
              ${this.icon('profile')}
              <span>${this.escapeHtml(this.t('profile'))}<br><small>${this.escapeHtml(this.t('profileSpec'))}</small></span>
              ${this.renderProfileMeasure()}
            </li>
            <li>${this.icon('wood')}<span>${this.escapeHtml(this.t('laminatedWood'))}</span></li>
            <li>${this.icon('craft')}<span>${this.escapeHtml(this.t('handStretched'))}</span></li>
            <li>${this.icon('swiss')}<span>${this.escapeHtml(this.t('directManufacturer'))}<br><small>${this.escapeHtml(this.t('fromSwitzerland'))}</small></span></li>
          </ul>
          <div class="fa-gp-price-values">
            <span>${this.escapeHtml(this.t('selectedTierPrice'))}</span>
            <strong>${this.formatMoney(selectedUnitPrice)}</strong>
            <small>${this.escapeHtml(tierLabel)} | ${this.quantity} ${this.escapeHtml(this.t('piece'))} ${this.escapeHtml(this.t('total'))} ${this.formatMoney(totalPrice)}</small>
            <small>${this.escapeHtml(this.t('normalPrice'))} ${this.formatMoney(this.config.price)} / ${this.escapeHtml(this.t('unitPrice'))}</small>
          </div>
        </section>
      `;
    }

    renderProfileMeasure() {
      if (!this.config.profileImage) return '';

      return `
        <span class="fa-gp-profile-measure" aria-label="${this.escapeHtml(this.t('frameHeight'))}">
          <span class="fa-gp-profile-measure__image">
            <img src="${this.escapeHtml(this.config.profileImage)}" alt="" width="58" height="40" loading="lazy" decoding="async">
          </span>
          <span class="fa-gp-profile-measure__marker" aria-hidden="true"><i></i></span>
          <b>4 cm</b>
        </span>
      `;
    }

    renderTierControls() {
      const rule = this.selectedTierRule();
      if (!rule) return '';

      const activeTier = this.applicableTier();
      const tiers = rule.tiers.map((tier) => {
        const isActive = activeTier && tier.minQuantity === activeTier.minQuantity;
        return `
          <button
            type="button"
            class="${isActive ? 'is-active' : ''}"
            data-fa-gp-tier="${tier.minQuantity}"
          >
            <span>${this.escapeHtml(this.tierLabel(tier))}</span>
            <strong>${this.formatMoney(tier.pricingValue)}</strong>
          </button>
        `;
      }).join('');

      return `
        <section class="fa-gp-tier-box" aria-label="${this.escapeHtml(this.t('tierPrices'))}">
          <label class="fa-gp-quantity">
            <span>${this.escapeHtml(this.t('quantity'))}</span>
            <input
              type="number"
              min="1"
              max="999"
              step="1"
              inputmode="numeric"
              value="${this.quantity}"
              data-fa-gp-quantity
              aria-label="${this.escapeHtml(this.t('tierQuantity'))}"
            >
          </label>
          <div class="fa-gp-tier-buttons">
            ${tiers}
          </div>
        </section>
      `;
    }

    renderProductionStatus() {
      return `
        <section class="fa-gp-status-box" aria-label="${this.escapeHtml(this.t('availability'))}">
          <div class="fa-gp-status">
            <span class="fa-gp-signal fa-gp-signal--production" aria-label="${this.escapeHtml(this.t('productionItem'))}"><i></i></span>
            <strong>${this.escapeHtml(this.t('productionItem'))}</strong>
          </div>
          <p>${this.escapeHtml(this.t('productionTiming'))}</p>
        </section>
      `;
    }

    renderDeliveryStatus() {
      return `
        <section class="fa-gp-delivery-box" aria-live="polite">
          <span>${this.escapeHtml(this.t('estimatedDelivery'))}</span>
          <strong>${this.escapeHtml(this.deliveryStatusText())}</strong>
        </section>
      `;
    }

    deliveryStatusText() {
      if (!this.isInStock()) return this.t('byAvailability');
      if (!this.config.deliveryEstimateUrl) return this.t('deliveryUnavailable');
      if (this.deliveryText) return this.deliveryText;
      if (this.deliveryError) return this.t('deliveryReload');
      return this.t('calculating');
    }

    async requestDeliveryEstimate() {
      if (this.deliveryRequested || !this.isInStock() || !this.config.deliveryEstimateUrl) return;

      this.deliveryRequested = true;
      this.deliveryLoading = true;

      try {
        const response = await fetch(this.config.deliveryEstimateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            sku: this.config.sku || '',
            thicknessCm: 4,
            productGroup: '',
            locale: this.locale,
            inventoryQuantity: this.readNumber(this.config.inventoryQuantity, 0),
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.ok) throw new Error(payload.message || 'Delivery estimate failed');

        const estimate = payload.estimate || {};
        this.deliveryText = String(estimate.deliveryText || '').trim() || this.t('byAvailability');
        this.deliveryError = false;
      } catch {
        this.deliveryText = '';
        this.deliveryError = true;
      } finally {
        this.deliveryLoading = false;
        this.render();
      }
    }

    setQuantity(rawQuantity, shouldHighlightCart) {
      const quantity = this.clamp(this.readNumber(rawQuantity, 1), 1, 999);
      this.quantity = quantity;
      this.syncNativeQuantity(quantity, shouldHighlightCart);
      this.render();
    }

    syncNativeQuantity(quantity, shouldHighlightCart) {
      const form = document.querySelector(`product-form-component[data-product-id="${this.config.productId}"]`);
      const selector = form?.querySelector('quantity-selector-component');
      const input = selector?.querySelector('input[name="quantity"]') || form?.querySelector('input[name="quantity"]');

      if (selector && typeof selector.setValue === 'function') {
        selector.setValue(String(quantity));
        if (typeof selector.onQuantityChange === 'function') selector.onQuantityChange();
        if (typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
      } else if (input) {
        input.value = String(quantity);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (shouldHighlightCart) {
        const button = form?.querySelector('.add-to-cart-button');
        if (button) {
          button.classList.add('fa-gp-cart-pulse');
          button.focus({ preventScroll: true });
          window.setTimeout(() => button.classList.remove('fa-gp-cart-pulse'), 1200);
        }
      }
    }

    renderLinkedPreviews() {
      document
        .querySelectorAll(`${PREVIEW_SELECTOR}[data-product-id="${this.config.productId}"]`)
        .forEach((preview) => {
          preview.innerHTML = this.renderPreview();
        });
    }

    bindStickyBuyBars() {
      document
        .querySelectorAll(`[data-fa-gp-sticky-bar][data-product-id="${this.config.productId}"]`)
        .forEach((bar) => {
          if (bar.dataset.faGpBound === 'true') return;
          bar.dataset.faGpBound = 'true';
          bar.querySelector('[data-fa-gp-sticky-cart]')?.addEventListener('click', () => {
            const form = document.querySelector(`product-form-component[data-product-id="${this.config.productId}"]`);
            const button = form?.querySelector('.add-to-cart-button:not(:disabled)');
            if (button) button.click();
          });
        });

      if (this.stickyBuyScrollBound) return;
      this.stickyBuyScrollBound = true;
      const update = () => this.updateStickyBuyBars();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }

    updateStickyBuyBars() {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const shouldShow = isMobile && window.scrollY > 520;
      document
        .querySelectorAll(`[data-fa-gp-sticky-bar][data-product-id="${this.config.productId}"]`)
        .forEach((bar) => {
          bar.classList.toggle('is-visible', shouldShow);
        });
    }

    renderPreview() {
      const shortSide = this.readNumber(this.config.shortSide, 0);
      const longSide = this.readNumber(this.config.longSide, 0);
      const thickness = this.readNumber(this.config.thicknessCm, 4);
      if (!shortSide || !longSide) return '';

      const length = Math.max(shortSide, longSide);
      const width = Math.min(shortSide, longSide);
      const maxSide = Math.max(length, width);
      const maxPreviewSize = window.matchMedia('(max-width: 749px)').matches ? 132 : 170;
      const previewWidth = (length / maxSide) * maxPreviewSize;
      const previewHeight = (width / maxSide) * maxPreviewSize;
      const previewRatio = length / width;
      const railSize = thickness === 4 ? 12 : 8;
      const labelWidth = Math.min(previewWidth * 0.42, 92);
      const supportType = this.supportTypeFor(length, width, thickness);
      const supportLabel = this.supportLabel(supportType);
      const style = `--preview-width: ${previewWidth.toFixed(2)}px; --preview-height: ${previewHeight.toFixed(2)}px; --preview-ratio: ${previewRatio.toFixed(4)}; --preview-rail: ${railSize}px; --preview-label-width: ${labelWidth.toFixed(2)}px;`;
      const labelImage = this.config.labelImage
        ? `<img class="fa-gp-preview-label" src="${this.escapeHtml(this.config.labelImage)}" alt="" width="92" height="62" aria-hidden="true" loading="lazy" decoding="async">`
        : '';

      return `
        <section class="fa-gp-preview" aria-label="${this.escapeHtml(this.t('previewAria'))}">
          <div class="fa-gp-preview-meta">
            <strong>${this.escapeHtml(this.t('preview'))}</strong>
            <span>${this.escapeHtml(shortSide)} x ${this.escapeHtml(longSide)} cm | ${this.escapeHtml(this.config.profileName || this.t('galleryQuality'))} | ${this.escapeHtml(thickness)} cm ${this.escapeHtml(this.t('thickness'))}</span>
          </div>
          <div class="fa-gp-preview-visuals">
            <div class="fa-gp-preview-stage">
              <div class="fa-gp-preview-measured" style="${style}">
                <span class="fa-gp-preview-measure fa-gp-preview-measure--top"><b>${this.escapeHtml(length)} cm</b></span>
                <span class="fa-gp-preview-measure fa-gp-preview-measure--left"><b>${this.escapeHtml(width)} cm</b></span>
                <div class="fa-gp-preview-frame">
                  <span class="fa-gp-preview-canvas"></span>
                  ${labelImage}
                </div>
              </div>
            </div>
            <div class="fa-gp-preview-stage fa-gp-preview-stage--back">
              <span class="fa-gp-back-label">${this.escapeHtml(supportLabel)}</span>
              ${this.renderBackPreview(previewWidth, previewHeight, previewRatio, railSize, supportType)}
            </div>
          </div>
        </section>
      `;
    }

    renderBackPreview(previewWidth, previewHeight, previewRatio, railSize, supportType) {
      const strutClass = previewWidth >= previewHeight
        ? 'fa-gp-back-strut fa-gp-back-strut--vertical'
        : 'fa-gp-back-strut fa-gp-back-strut--horizontal';
      const struts = {
        none: '',
        querleiste: `<span class="${strutClass}"></span>`,
        kreuz: '<span class="fa-gp-back-strut fa-gp-back-strut--horizontal"></span><span class="fa-gp-back-strut fa-gp-back-strut--vertical"></span>',
        doppelkreuz: '<span class="fa-gp-back-strut fa-gp-back-strut--horizontal"></span><span class="fa-gp-back-strut fa-gp-back-strut--vertical fa-gp-back-strut--left"></span><span class="fa-gp-back-strut fa-gp-back-strut--vertical fa-gp-back-strut--right"></span>',
      }[supportType] || '';

      return `
        <div
          class="fa-gp-back-frame fa-gp-back-frame--${this.escapeHtml(supportType)}"
          style="--preview-width: ${previewWidth.toFixed(2)}px; --preview-height: ${previewHeight.toFixed(2)}px; --preview-ratio: ${previewRatio.toFixed(4)}; --preview-rail: ${railSize}px;"
        >
          <span class="fa-gp-back-canvas"></span>
          ${struts}
        </div>
      `;
    }

    supportTypeFor(length, width) {
      const longSide = Math.max(length, width);
      const shortSide = Math.min(length, width);
      const sidesAtLeast50 = (length >= 50 ? 1 : 0) + (width >= 50 ? 1 : 0);

      if (longSide >= 220) return 'doppelkreuz';
      if (sidesAtLeast50 >= 2) return 'kreuz';
      if (sidesAtLeast50 === 1 || (longSide >= 120 && shortSide >= 20 && shortSide <= 40)) return 'querleiste';
      return 'none';
    }

    supportLabel(type) {
      return {
        none: this.t('supportNone'),
        querleiste: this.t('supportBar'),
        kreuz: this.t('supportCross'),
        doppelkreuz: this.t('supportDoubleCross'),
      }[type] || this.t('supportAuto');
    }

    tierLabel(tier) {
      const sourceLabel = String(tier.vpeLabel || '').trim();
      const exactQuantity = sourceLabel.match(/^(\d+)\s+Stück$/i);
      const fromQuantity = sourceLabel.match(/^ab\s+(\d+)\s+Stück$/i);
      if (fromQuantity) return this.t('fromQuantity', { quantity: fromQuantity[1] });
      if (exactQuantity) {
        return `${exactQuantity[1]} ${this.t(Number(exactQuantity[1]) === 1 ? 'piece' : 'pieces')}`;
      }
      if (sourceLabel) return sourceLabel;
      return tier.minQuantity === 1 ? `1 ${this.t('piece')}` : this.t('fromQuantity', { quantity: tier.minQuantity });
    }

    icon(type) {
      if (type === 'profile') {
        return `
          <span class="fa-gp-profile-icon fa-gp-profile-icon--benefit" aria-hidden="true">
            <span class="fa-gp-profile-icon__face"></span>
            <span class="fa-gp-profile-icon__depth"></span>
            <strong>4 cm</strong>
          </span>
        `;
      }

      const icons = {
        delivery: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 6h11v9H3z"/><path d="M14 9h4l3 3v3h-7z"/><circle cx="7" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></svg>',
        payment: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/><path d="M7 15h5"/></svg>',
        craft: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 19h14"/><path d="M7 19V7l5-3 5 3v12"/><path d="M9 10h6"/><path d="M9 14h6"/></svg>',
        wood: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/><path d="M7 5c2 1 4 1 6 0"/><path d="M11 10c2 1 4 1 6 0"/><path d="M6 15c3 1 6 1 9 0"/></svg>',
        swiss: '<span class="fa-gp-swiss-cross" aria-hidden="true"><i></i></span>',
      };

      return `<span class="fa-gp-benefit-icon">${icons[type] || ''}</span>`;
    }

    formatMoney(cents) {
      const currency = String(this.config.currency || 'CHF').toUpperCase();
      const amount = this.readNumber(cents, 0) / 100;
      const sign = amount < 0 ? '-' : '';
      const [whole, decimals] = Math.abs(amount).toFixed(2).split('.');
      return `${currency} ${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${decimals}`;
    }

    readNumber(value, fallback) {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : fallback;
    }

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }

    t(key, vars = {}) {
      let text = this.i18n[key] || key;
      Object.entries(vars).forEach(([name, value]) => {
        text = text.replaceAll(`{${name}}`, String(value ?? ''));
      });
      return text;
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  if (!customElements.get(TOOL_SELECTOR)) {
    customElements.define(TOOL_SELECTOR, GalerieProductTools);
  }
})();
